/**
 * The mail this system sends, as a closed set.
 *
 * `.docs/04-auth-clients.md` DRY watchlist: "The seven notification kinds are one
 * enum in `packages/shared/src/schemas/notifications.ts` used by the table, the
 * routes and, in 06, the templates."
 *
 * The list, the switchable flag and the defaults all come from
 * `mockups/client/settings.html` — the seven rows under *Email notifications*, the
 * "cannot be turned off" note on `payment_failed`, and the sentence above them:
 * "Every notification about money is on. The weekly digest and the widget-cap
 * warning are off."
 *
 * `ARCHITECTURE.md` §Integrations says the same thing from the other side:
 * "Notification preferences are checked at enqueue time; 'payment failed' ignores
 * them."
 */
import { z } from 'zod';
export const NOTIFICATION_KINDS = {
    /** With the PDF attached (`settings.html`). */
    invoice_issued: { switchable: true, enabledByDefault: true },
    /** After a payment settles. */
    payment_receipt: { switchable: true, enabledByDefault: true },
    /** A failed charge or a scheduled retry. Not switchable. */
    payment_failed: { switchable: false, enabledByDefault: true },
    /** Quotes, extras, package changes, sign-offs. */
    approval_requested: { switchable: true, enabledByDefault: true },
    /** When files land in a month folder. */
    deliverable_published: { switchable: true, enabledByDefault: true },
    /** Monday morning, one email. */
    weekly_digest: { switchable: true, enabledByDefault: false },
    /** When a widget stops serving. */
    widget_cap_reached: { switchable: true, enabledByDefault: false },
};
/** In display order — the order `settings.html` lists them. */
export const NOTIFICATION_KIND_NAMES = Object.keys(NOTIFICATION_KINDS);
/** The kinds a member may switch off. `payment_failed` is not one of them. */
export const SWITCHABLE_NOTIFICATION_KINDS = NOTIFICATION_KIND_NAMES.filter((kind) => NOTIFICATION_KINDS[kind].switchable);
/** True when a member with no stored preference should receive this kind. */
export function notificationDefault(kind) {
    return NOTIFICATION_KINDS[kind].enabledByDefault;
}
/** True when the client is allowed to turn this kind off at all. */
export function isSwitchableNotification(kind) {
    return NOTIFICATION_KINDS[kind].switchable;
}
export const NotificationKindSchema = z
    .enum(NOTIFICATION_KIND_NAMES)
    .describe('Which automated email this preference is about. The set is closed: invoice_issued, ' +
    'payment_receipt, payment_failed, approval_requested, deliverable_published, ' +
    'weekly_digest, widget_cap_reached.');
/**
 * The whole preferences page as one object: every switchable kind, each a boolean.
 *
 * `payment_failed` is deliberately absent, and the object is **strict**: a body that
 * carried it would be a request to switch off the mail about a failed charge, and
 * the API answers that with a validation error rather than by quietly dropping the
 * key and answering 200 to somebody who thinks they turned it off.
 */
export const NotificationPrefsSchema = z
    .strictObject(Object.fromEntries(SWITCHABLE_NOTIFICATION_KINDS.map((kind) => [
    kind,
    z.boolean().describe(`Whether to send the ${kind} email.`),
])))
    .describe('One switch per notification kind. payment_failed is not switchable and is not here.');
//# sourceMappingURL=notifications.js.map